import Phaser from "phaser";
import { AssetLocator, Animation, DEFAULT_ANIMATION } from "./AssetLocator";

const FRAME_INDEX_RE = /_(\d+)\.png$/;

export const PRIORITY_ANIMATIONS: Animation[] = ["idle", "fly", "stunned"];

/**
 * Queue atlas loads for all cat body parts and accessories across the given
 * animation names so PreloadScene can do a single bulk load.
 */
export function queueAllAssets(
  scene: Phaser.Scene,
  animations: Animation[] = PRIORITY_ANIMATIONS,
): void {
  const catNumbers = Array.from({ length: 15 }, (_, i) => i + 1);
  catNumbers.forEach((num) => {
    const locator = new AssetLocator(num);
    animations.forEach((anim) => {
      Object.values(locator.getAllBodyAssets(anim)).forEach((asset) => {
        scene.load.atlas(
          asset.key,
          `assets/${asset.imagePath}`,
          `assets/${asset.jsonPath}`,
        );
      });
      // Queue clothes/hat/glasses generically – cheap duplicates are OK in preload
      AssetLocator.getAvailableAccessories("clothes").forEach((name) => {
        const a = locator.getClothesAssets(name, anim);
        scene.load.atlas(
          a.back.key,
          `assets/${a.back.imagePath}`,
          `assets/${a.back.jsonPath}`,
        );
        if (a.front.key !== a.back.key) {
          scene.load.atlas(
            a.front.key,
            `assets/${a.front.imagePath}`,
            `assets/${a.front.jsonPath}`,
          );
        }
      });
      ["hat", "glasses"].forEach((type) => {
        AssetLocator.getAvailableAccessories(type as "hat" | "glasses").forEach(
          (name) => {
            const a = locator.getAccessoryAsset(type as any, name, anim);
            scene.load.atlas(
              a.key,
              `assets/${a.imagePath}`,
              `assets/${a.jsonPath}`,
            );
          },
        );
      });
    });
  });
}

/**
 * Load assets for a single cat (number) and animation at runtime then create
 * synced animations. Returns a promise that resolves after load complete.
 */
export function loadAssetsForCat(
  scene: Phaser.Scene,
  catNumber: number,
  animation: Animation = DEFAULT_ANIMATION,
): Promise<void> {
  const locator = new AssetLocator(catNumber);
  Object.values(locator.getAllBodyAssets(animation)).forEach((asset) => {
    scene.load.atlas(
      asset.key,
      `assets/${asset.imagePath}`,
      `assets/${asset.jsonPath}`,
    );
  });
  AssetLocator.getAvailableAccessories("clothes").forEach((name) => {
    const a = locator.getClothesAssets(name, animation);
    scene.load.atlas(
      a.back.key,
      `assets/${a.back.imagePath}`,
      `assets/${a.back.jsonPath}`,
    );
    if (a.front.key !== a.back.key) {
      scene.load.atlas(
        a.front.key,
        `assets/${a.front.imagePath}`,
        `assets/${a.front.jsonPath}`,
      );
    }
  });
  ["hat", "glasses"].forEach((type) => {
    AssetLocator.getAvailableAccessories(type as "hat" | "glasses").forEach(
      (name) => {
        const a = locator.getAccessoryAsset(type as any, name, animation);
        scene.load.atlas(
          a.key,
          `assets/${a.imagePath}`,
          `assets/${a.jsonPath}`,
        );
      },
    );
  });

  return new Promise((res) => {
    scene.load.once("complete", () => {
      createAnimationsForCat(scene, catNumber, animation);
      res();
    });
    scene.load.start();
  });
}

/**
 * After atlases are loaded this creates Phaser animations for body parts and
 * accessories keeping frame-rates in sync even when sheets differ in length.
 */
export function createAnimationsForCat(
  scene: Phaser.Scene,
  catNumber: number,
  animation: Animation,
): void {
  const locator = new AssetLocator(catNumber);
  const assets = locator.getAllBodyAssets(animation);

  const frameNameCache: Record<
    string,
    Phaser.Types.Animations.AnimationFrame[]
  > = {};
  const getFrames = (key: string) =>
    (frameNameCache[key] ||= scene.anims.generateFrameNames(key));

  const maxFrames = Math.max(
    ...Object.values(assets).map((a) => getFrames(a.key).length),
  );
  const baseFps = 24;
  const createAnim = (
    key: string,
    frames: Phaser.Types.Animations.AnimationFrame[],
  ) => {
    if (scene.anims.exists(key)) return;
    const frameRate = Math.round((frames.length / maxFrames) * baseFps) || 1;
    scene.anims.create({ key, frames, frameRate, repeat: -1 });
  };

  Object.values(assets).forEach((a) => createAnim(a.key, getFrames(a.key)));

  // Accessories
  const accKeys: string[] = [];
  AssetLocator.getAvailableAccessories("clothes").forEach((name) => {
    const a = locator.getClothesAssets(name, animation);
    accKeys.push(a.back.key, a.front.key);
  });
  ["hat", "glasses"].forEach((type) => {
    AssetLocator.getAvailableAccessories(type as "hat" | "glasses").forEach(
      (name) => {
        accKeys.push(
          locator.getAccessoryAsset(type as any, name, animation).key,
        );
      },
    );
  });
  accKeys.forEach((key) => {
    if (scene.anims.exists(key)) return;
    const frames = scene.anims.generateFrameNames(key).sort((a, b) => {
      const idx = (f: Phaser.Types.Animations.AnimationFrame) =>
        Number(String(f.frame).match(FRAME_INDEX_RE)?.[1] ?? "0");
      return idx(a) - idx(b);
    });
    scene.anims.create({ key, frames, frameRate: 24, repeat: -1 });
  });
}

/** Convenience: bulk create animations for every cat & selected animations */
export function createAllAnimations(
  scene: Phaser.Scene,
  animations: Animation[] = PRIORITY_ANIMATIONS,
): void {
  const catNumbers = Array.from({ length: 15 }, (_, i) => i + 1);
  catNumbers.forEach((num) => {
    animations.forEach((anim) => createAnimationsForCat(scene, num, anim));
  });
}
